import advanceReportingConstants from "@/constants/advanceReportingConstants"
import schoolYearRetention from "@/helpers/schoolYearRetention"
import moment from "moment-timezone"

const DATE_DISPLAY_FORMAT = "MM/DD/YYYY"

const formatRange = (start, end) => {
  return {
    startDate: start.format(DATE_DISPLAY_FORMAT),
    endDate: end.format(DATE_DISPLAY_FORMAT)
  }
}

const clampRangeToSchoolYear = (start, end, minDate, maxDate) => {
  const adjustedStart =
    minDate && start.isBefore(minDate) ? minDate.clone() : start.clone()
  const adjustedEnd =
    maxDate && end.isAfter(maxDate) ? maxDate.clone() : end.clone()

  if (adjustedStart.isAfter(adjustedEnd)) {
    return formatRange(adjustedStart, adjustedStart)
  }

  return formatRange(adjustedStart, adjustedEnd)
}

export const getMinDate = (schoolYear, activeSchoolTimeZone, school) => {
  const timeZone = activeSchoolTimeZone || moment.tz.guess()

  if (schoolYear?.value) {
    const yearMin = moment.tz(`${schoolYear?.value?.[0]}-07-01`, timeZone)
    const cutoff = schoolYearRetention.parseLastDateToKeepData(school)

    if (cutoff && cutoff.isAfter(yearMin)) {
      return cutoff.clone().tz(timeZone)
    }

    return yearMin
  }
  return moment().tz(timeZone)
}

export const getMaxDate = (schoolYear, activeSchoolTimeZone) => {
  const timeZone = activeSchoolTimeZone || moment.tz.guess()

  if (schoolYear?.value) {
    return moment.tz(`${schoolYear?.value?.[1]}-06-30`, timeZone)
  }
  return moment().tz(timeZone)
}

/**
 * Map today into the selected school year the same way History does.
 * July–December uses the start year. January–June uses the end year.
 */
export const getSelectedYearBaseDate = (schoolYear, activeSchoolTimeZone) => {
  const timeZone = activeSchoolTimeZone || moment.tz.guess()
  const today = moment().tz(timeZone)

  if (!schoolYear?.value?.length) {
    return today
  }

  const [startYear, endYear] = schoolYear.value
  const selectedYear =
    today.month() >= 6 && today.month() <= 11 ? startYear : endYear

  return today.clone().year(selectedYear)
}

/**
 * Move a calendar date into the selected school year.
 * July–December uses the start year. January–June uses the end year.
 */
export const remapDateToSchoolYear = (date, schoolYear) => {
  const parsedDate = moment.isMoment(date)
    ? date.clone()
    : moment(date, ["YYYY-MM-DD", DATE_DISPLAY_FORMAT], true)

  if (!parsedDate.isValid() || !schoolYear?.value?.length) {
    return parsedDate
  }

  const [startYear, endYear] = schoolYear.value
  const selectedYear = parsedDate.month() >= 6 ? startYear : endYear

  return parsedDate.clone().year(selectedYear)
}

export const remapCustomRangeToSchoolYear = (
  startDate,
  endDate,
  schoolYear,
  minDate,
  maxDate
) => {
  const remappedStart = remapDateToSchoolYear(startDate, schoolYear)
  const remappedEnd = remapDateToSchoolYear(endDate, schoolYear)

  if (!remappedStart?.isValid?.() || !remappedEnd?.isValid?.()) {
    return null
  }

  return clampRangeToSchoolYear(remappedStart, remappedEnd, minDate, maxDate)
}

export const getPreviousDayRange = (
  schoolYear,
  activeSchoolTimeZone,
  minDate
) => {
  const yesterday = getSelectedYearBaseDate(
    schoolYear,
    activeSchoolTimeZone
  ).subtract(1, "days")
  const adjustedStart =
    minDate && yesterday.isBefore(minDate) ? minDate.clone() : yesterday

  return formatRange(adjustedStart, adjustedStart)
}

export const getThisWeekRange = (
  minDate,
  maxDate,
  schoolYear,
  activeSchoolTimeZone
) => {
  const baseDate = getSelectedYearBaseDate(schoolYear, activeSchoolTimeZone)
  const weekStart = baseDate.clone().startOf("week")
  const weekEnd = baseDate.clone().endOf("week")

  return clampRangeToSchoolYear(weekStart, weekEnd, minDate, maxDate)
}

export const getThisMonthRange = (
  minDate,
  maxDate = null,
  schoolYear = null,
  activeSchoolTimeZone = null
) => {
  const baseDate = getSelectedYearBaseDate(schoolYear, activeSchoolTimeZone)
  const monthStart = baseDate.clone().startOf("month")
  const monthEnd = baseDate.clone().endOf("month")

  return clampRangeToSchoolYear(monthStart, monthEnd, minDate, maxDate)
}

export const getThisYearRange = (minDate, maxDate) => {
  return formatRange(minDate, maxDate)
}

export const getCustomDateRangeConstraints = (minDate, maxDate) => {
  const yesterday = moment().subtract(1, "days")
  const adjustedEndDate = yesterday.isBefore(maxDate) ? yesterday : maxDate

  return {
    minYear: minDate.year(),
    maxYear: adjustedEndDate.year(),
    startDate: minDate.format(DATE_DISPLAY_FORMAT),
    endDate: adjustedEndDate.format(DATE_DISPLAY_FORMAT)
  }
}

export const calculatePreviousDatesForMonth = (startDate) => {
  if (!startDate) {
    return { previousStartDate: null, previousEndDate: null }
  }

  const currentStart = moment(startDate, "YYYY-MM-DD")
  const currentMonthStart = currentStart.clone().startOf("month")
  const currentMonthEnd = currentStart.clone().endOf("month")
  const previousMonthStart = currentMonthStart
    .clone()
    .subtract(1, "month")
    .startOf("month")
  const previousMonthEnd = currentMonthEnd
    .clone()
    .subtract(1, "month")
    .endOf("month")

  return {
    previousStartDate: previousMonthStart.format("YYYY-MM-DD"),
    previousEndDate: previousMonthEnd.format("YYYY-MM-DD")
  }
}

export const getThisMonthDateRangeForStore = (
  schoolYear,
  activeSchoolTimeZone,
  school
) => {
  const minDate = getMinDate(schoolYear, activeSchoolTimeZone, school)
  const maxDate = getMaxDate(schoolYear, activeSchoolTimeZone)
  const thisMonthRange = getThisMonthRange(
    minDate,
    maxDate,
    schoolYear,
    activeSchoolTimeZone
  )

  const startDate = moment(thisMonthRange.startDate, "MM/DD/YYYY").format(
    "YYYY-MM-DD"
  )
  const endDate = moment(thisMonthRange.endDate, "MM/DD/YYYY").format(
    "YYYY-MM-DD"
  )

  const { previousStartDate, previousEndDate } =
    calculatePreviousDatesForMonth(startDate)

  return {
    type: advanceReportingConstants.DATE_RANGE_TYPES.THIS_MONTH,
    startDate,
    endDate,
    previousStartDate,
    previousEndDate
  }
}
